import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  LOCALE_ID,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideAnimations,
} from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withHashLocation,
} from '@angular/router';
import {
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';

import { environment } from '../environments/environment';
import { AuthInterceptor } from '../shared/interceptors/auth.interceptor';
import { routes } from './app.routes';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

const _apiUrl = environment.apiUrl;
const _clientIdAzure = environment.azure.clientId;
const _authorityAzure = `https://login.microsoftonline.com/${environment.azure.tenantId}`;
const _redirectAplicationAzure = environment.azure.redirectUri;
const _scopesAzure = environment.azure.scopes;

/**
 * Crea la instancia de PublicClientApplication para MSAL.
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: _clientIdAzure,
      authority: _authorityAzure,
      redirectUri: _redirectAplicationAzure,
    },
  });
}

/**
 * Configura el interceptor para MSAL.
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(_apiUrl, _scopesAzure);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    authRequest: {
      extraScopesToConsent: _scopesAzure,
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, withHashLocation(), withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    { provide: LOCALE_ID, useValue: 'es-MX' },
    providePrimeNG({
      theme: {
        preset: Aura, // We'll keep it simple here as it's also set in AppComponent via setupPrimeNGConfig
        options: {
          prefix: 'p',
          darkModeSelector: '.dark',
          cssLayer: {
            name: 'primeng',
            order: 'base, primeng, utilities',
          },
        },
      },
      translation: {
        // Mensajes para multiSelect
        emptyMessage: 'No hay datos disponibles',
        emptyFilterMessage: 'No se encontraron resultados',
        selectionMessage: '{0} elementos seleccionados',
        // Mensajes para autoComplete
        emptySearchMessage: 'No se encontraron resultados',
      },
    }),
    MsalService,
    MsalBroadcastService,
    /**
     * Se provee la instancia de MSAL creada por la MSALInstanceFactory
     */
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },

    /**
     * Se provee la configuración para el interceptor de MSAL
     */
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },

    /**
     * Provisión del interceptor MSAL a la aplicación.
     * 'multi: true' indica que pueden existir múltiples interceptores.
     */
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },

    /**
     * Providers para los servicios de mensajes y confirmación de PrimeNG
     * para poder usarlos en toda la aplicación.
     */
    MessageService,
    ConfirmationService,
  ],
};
